/**
 * Inclusão rápida de itens do pedido (v6): valida SÓ o item que está sendo incluído, decide se
 * soma a uma linha existente (D10) e calcula o valor da linha. Mesma aritmética decimal do
 * banco (texto, sem ponto flutuante). Usado pelo Pedido de Venda e, depois, pela Venda Direta.
 */
import { parseDecimalBR } from "./decimal-input";
import { compareMoney, lineValue, money, subtractMoney } from "./money";
import { compareDecimal, fromScaled, multiplyDecimal, toScaled } from "./quantities";

/** Item já incluído na tabela do pedido (ainda não salvo). */
export type SaleDraftItem = {
  /** identificador do rascunho do item (nunca repetido: protege contra inclusão dupla) */
  key: string;
  productId: string;
  productCode: string;
  productName: string;
  unitId: string;
  unitCode: string;
  /** quantidade canônica ("10.5") */
  quantity: string;
  /** preço digitado canônico, ou null = preço da tabela */
  unitPrice: string | null;
  /** preço da tabela do cliente para a unidade (null = fora da tabela) */
  listPrice: string | null;
  maxDiscountPercent: string | null;
  /** desconto em R$ canônico ("0" sem desconto) */
  discount: string;
  notes: string | null;
};

/** O que a pessoa digitou na área de inclusão. */
export type SaleEntryInput = {
  key: string;
  productId: string;
  productCode: string;
  productName: string;
  unitId: string;
  unitCode: string;
  /** casas decimais permitidas na unidade escolhida */
  unitDecimals: number;
  quantity: string;
  unitPrice: string;
  discount: string;
  notes?: string;
  /** resultado da consulta de preço: undefined = ainda consultando */
  priceInfo: { listPrice: string | null; maxDiscountPercent: string | null } | undefined;
};

export type SaleEntryField = "productId" | "unitId" | "quantity" | "unitPrice" | "discount";

export type SaleEntryResult =
  | { ok: true; item: SaleDraftItem }
  | { ok: false; pending: true }
  | {
      ok: false;
      pending?: false;
      errors: Partial<Record<SaleEntryField, string>>;
      first: SaleEntryField;
    };

const FIELDS: SaleEntryField[] = ["productId", "unitId", "quantity", "unitPrice", "discount"];

/** Preço praticado (digitado ou o da tabela) ou null se não houver. */
export const effectivePrice = (i: Pick<SaleDraftItem, "unitPrice" | "listPrice">) =>
  i.unitPrice ?? i.listPrice;

/** Valor da linha: quantidade × preço − desconto (null sem preço). */
export function saleLineTotal(i: SaleDraftItem): string | null {
  const price = effectivePrice(i);
  return price === null ? null : subtractMoney(lineValue(i.quantity, price), i.discount);
}

/** Valida o item da área de inclusão. Nada é incluído com erro ou com o preço por consultar. */
export function validateSaleEntry(e: SaleEntryInput): SaleEntryResult {
  const errors: Partial<Record<SaleEntryField, string>> = {};
  if (!e.productId) errors.productId = "Escolha o produto.";
  if (e.productId && !e.unitId) errors.unitId = "Escolha a unidade.";
  const qty = parseDecimalBR(e.quantity, "numero");
  if (e.quantity.trim() === "") errors.quantity = "Informe a quantidade.";
  else if (qty === null) errors.quantity = "Quantidade inválida. Exemplo: 10 ou 2,5.";
  else if (compareDecimal(qty, 0) <= 0)
    errors.quantity = "A quantidade precisa ser maior que zero.";
  else if (decimals(qty) > e.unitDecimals) {
    errors.quantity =
      e.unitDecimals === 0
        ? "Esta unidade não aceita casas decimais."
        : `Esta unidade aceita no máximo ${e.unitDecimals} casa(s) decimal(is).`;
  }
  let price: string | null = null;
  if (e.unitPrice.trim() !== "") {
    price = parseDecimalBR(e.unitPrice, "dinheiro");
    if (price === null) errors.unitPrice = "Preço inválido. Exemplo: 10,50.";
    else if (decimals(price) > 4) errors.unitPrice = "Use no máximo 4 casas decimais no preço.";
  }
  const discountText = e.discount.trim();
  const discount = discountText === "" ? "0" : parseDecimalBR(discountText, "dinheiro");
  if (discount === null) errors.discount = "Desconto inválido. Exemplo: 5,00.";
  else if (decimals(discount) > 2) errors.discount = "Use no máximo 2 casas decimais no desconto.";

  const firstError = FIELDS.find((f) => errors[f] !== undefined);
  if (firstError) return { ok: false, errors, first: firstError };
  if (e.priceInfo === undefined) return { ok: false, pending: true };

  const listPrice = e.priceInfo.listPrice;
  if (price === null && listPrice === null) {
    return {
      ok: false,
      errors: { unitPrice: "Produto fora da tabela do cliente: informe o preço." },
      first: "unitPrice",
    };
  }
  const item: SaleDraftItem = {
    key: e.key,
    productId: e.productId,
    productCode: e.productCode,
    productName: e.productName,
    unitId: e.unitId,
    unitCode: e.unitCode,
    quantity: qty!,
    unitPrice: price,
    listPrice,
    maxDiscountPercent: e.priceInfo.maxDiscountPercent,
    discount: discount!,
    notes: e.notes?.trim() ? e.notes.trim() : null,
  };
  const value = lineValue(item.quantity, effectivePrice(item)!);
  if (compareMoney(item.discount, value) > 0) {
    return {
      ok: false,
      errors: { discount: "O desconto não pode ser maior que o valor do item." },
      first: "discount",
    };
  }
  return { ok: true, item };
}

const decimals = (canonical: string) => {
  const i = canonical.indexOf(".");
  return i < 0 ? 0 : canonical.length - i - 1;
};

const samePrice = (a: string | null, b: string | null) =>
  a === null || b === null ? a === b : compareDecimal(a, b) === 0;

/**
 * Inclui o item na tabela (D10): soma a quantidade numa linha com o MESMO produto, unidade e
 * preço quando nenhuma das duas tem desconto; senão, nova linha. O mesmo `key` nunca entra
 * duas vezes (Enter repetido, Enter + clique): a segunda chamada não muda nada.
 */
export function mergeSaleEntry(
  items: readonly SaleDraftItem[],
  item: SaleDraftItem,
): {
  items: SaleDraftItem[];
  outcome: "repetido" | "somado" | "nova_linha" | "incluido";
  index: number;
} {
  const existing = items.findIndex((i) => i.key === item.key);
  if (existing >= 0) return { items: [...items], outcome: "repetido", index: existing };
  const noDiscount = (i: SaleDraftItem) => compareMoney(i.discount, 0) === 0;
  const same = items.findIndex(
    (i) =>
      i.productId === item.productId &&
      i.unitId === item.unitId &&
      samePrice(i.unitPrice, item.unitPrice) &&
      noDiscount(i) &&
      noDiscount(item),
  );
  if (same >= 0) {
    const base = items[same]!;
    const summed = {
      ...base,
      quantity: fromScaled(toScaled(base.quantity) + toScaled(item.quantity)),
    };
    return {
      items: items.map((i, n) => (n === same ? summed : i)),
      outcome: "somado",
      index: same,
    };
  }
  const otherCondition = items.some((i) => i.productId === item.productId);
  return {
    items: [...items, item],
    outcome: otherCondition ? "nova_linha" : "incluido",
    index: items.length,
  };
}

/** Avisos que não impedem a inclusão (seguem para a aprovação, como antes). */
export function saleItemWarnings(
  i: SaleDraftItem,
  available?: string | null,
  quantityBase?: string,
  /** v7: quem pode vender sem estoque recebe um aviso informativo (não bloqueia) */
  stock?: { canSellShort: boolean; unitCode: string },
): string[] {
  const out: string[] = [];
  if (i.listPrice === null) out.push("Fora da tabela: exige liberação de preço na aprovação.");
  else if (i.maxDiscountPercent !== null) {
    const price = effectivePrice(i)!;
    const net = subtractMoney(lineValue(i.quantity, price), i.discount);
    const list = lineValue(i.quantity, i.listPrice);
    // mesmo limite do banco: tabela × (1 − desconto máximo), arredondado a 2 casas
    const keep = fromScaled(toScaled("100") - toScaled(i.maxDiscountPercent));
    const floor = money(multiplyDecimal(multiplyDecimal(list, keep), "0.01"));
    if (compareMoney(net, floor) < 0) {
      out.push(`Desconto acima do máximo da tabela (${i.maxDiscountPercent.replace(".", ",")}%).`);
    }
  }
  if (available !== undefined && available !== null && quantityBase !== undefined) {
    if (compareDecimal(quantityBase, available) > 0) {
      out.push(
        stock?.canSellShort
          ? shortageNotice(quantityBase, available, stock.unitCode)
          : "Disponível no depósito menor que a quantidade.",
      );
    }
  }
  return out;
}

/**
 * v7: aviso da venda sem estoque (quem tem a permissão): quanto há e quanto fica a regularizar.
 * Quantidades na unidade-base (texto com ponto).
 */
export function shortageNotice(needed: string, available: string, unitCode: string): string {
  const free = compareDecimal(available, "0") > 0 ? available : "0";
  const short = fromScaled(toScaled(needed) - toScaled(free));
  const br = (v: string) => v.replace(".", ",");
  return `Saldo disponível: ${br(free)} ${unitCode}. A venda deixará ${br(short)} ${unitCode} a regularizar neste depósito (permitido pelo seu perfil).`;
}
